import React, { FC, useState } from 'react'
import { Box, Collapse, SvgIcon, Typography, makeStyles } from '@material-ui/core'
import { Link, generatePath } from 'react-router-dom'
import { ReactComponent as CircleArrowBottomIcon } from 'assets/svg/CircleArrowBottomIcon.svg'
import { KB_VIEW_URL } from 'constants/routes'
import { IArticleListItem, ISection } from 'typescript/interfaces/knowledgeBase'
import { bg3 } from 'containers/MaterialUiContainer/mainTheme'

interface IKnowledgeBaseNavigation {
  sections: Array<ISection>
  articles: Array<IArticleListItem>
  activeArticleId?: number
}

const useStyles = makeStyles((theme) => ({
  panel: {
    width: '17rem',
    flexShrink: 0,
    backgroundColor: bg3,
    padding: '1.5rem 1rem',
  },
  sectionRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '0.6rem 0.75rem',
    marginBottom: '0.5rem',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
  },
  arrow: {
    transition: 'transform 0.2s',
    marginLeft: '0.5rem',
  },
  arrowCollapsed: {
    transform: 'rotate(-90deg)',
  },
  pageRow: {
    display: 'block',
    margin: '0 0.75rem 0.4rem 0.75rem',
    padding: '0.45rem 0.75rem',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    textDecoration: 'none',
    '&:hover': {
      borderColor: theme.palette.secondary.main,
      color: theme.palette.text.secondary,
    },
  },
  activePageRow: {
    borderColor: theme.palette.secondary.main,
    color: theme.palette.text.secondary,
  },
  emptySection: {
    margin: '0 0.75rem 0.75rem 1.5rem',
    color: theme.palette.text.disabled,
  },
}))

const KnowledgeBaseNavigation: FC<IKnowledgeBaseNavigation> = ({ sections, articles, activeArticleId }) => {
  const s = useStyles()
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({})

  const toggleSection = (sectionId: number) => setCollapsed((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }))

  return (
    <Box className={s.panel}>
      {sections.map((section) => {
        const sectionPages = articles
          .filter((article) => article.sectionId === section.id)
          .sort((first, second) => first.title.localeCompare(second.title))
        const isOpen = !collapsed[section.id]

        return (
          <Box key={section.id} mb="1rem">
            <Box component="button" type="button" className={s.sectionRow} onClick={() => toggleSection(section.id)}>
              <Typography variant="body1" color="textSecondary">
                {section.name}
              </Typography>
              <SvgIcon
                viewBox="0 0 20 20"
                component={CircleArrowBottomIcon}
                className={`${s.arrow} ${isOpen ? '' : s.arrowCollapsed}`}
              />
            </Box>
            <Collapse in={isOpen}>
              {sectionPages.length === 0 ? (
                <Typography variant="caption" className={s.emptySection}>
                  No pages yet
                </Typography>
              ) : (
                sectionPages.map((article) => (
                  <Typography
                    key={article.id}
                    variant="caption"
                    component={Link}
                    to={generatePath(KB_VIEW_URL, { id: article.id })}
                    className={`${s.pageRow} ${article.id === activeArticleId ? s.activePageRow : ''}`}
                  >
                    {article.title}
                  </Typography>
                ))
              )}
            </Collapse>
          </Box>
        )
      })}
    </Box>
  )
}

export default KnowledgeBaseNavigation
